/**
 * @file src/local/localLibrary.js
 * @description The local audio library: a user's own files, played from where they live.
 *
 * Everything the rest of the app needs from the library goes through here. The store keeps the
 * metadata (`localLibraryStore.js`), the platform layer reaches the file (`localFileAccess.js`),
 * the queue runs the analysis (`analysis/`), and the byte registry (`localByteSources.js`) is the
 * one door the audio engine knocks on. This module wires them and holds the live picture: which
 * entries exist, which can be reached right now, which are still being analysed.
 *
 * ## How an entry becomes playable
 *
 * The engine only knows how to boot a stage from a track release. So each local entry is given a
 * SYNTHETIC release — a payload `normalizeTrackRelease` accepts, carrying the file's name, its
 * duration, its loudness gain (in the field the adapter already reads), a `blob:` URL for its peaks
 * (in the field the waveform already fetches), and the default orbiter and world as a hydrated
 * pair — and that payload is primed into the release cache under the entry's `local:` id. From
 * then on a local card is dropped, swapped and booted by exactly the code a hosted card uses; the
 * only thing that differs is where `resolvePlaybackAudioSource` gets its bytes, and that is the
 * registry.
 *
 * ## The copy, and what it owes
 *
 * Outside the desktop shell the file is copied once into this browser's own private storage
 * (`localByteVault.js`) so the library survives a reload on every browser and every phone, not
 * only the two that can remember a pointer. That copy is a debt this module services: made on
 * import, deleted with the entry, swept when orphaned on boot, counted for the rail, and described
 * to the person whose disk it is on. The desktop shell copies nothing — it has the real file.
 *
 * ## One file, many collections
 *
 * An entry belongs to a SET of collections (`entry.scopes`), not to one. Dropping the same file
 * into a second collection adds a scope to the entry that already holds it; it does not mint a
 * second entry, and it does not take a second copy of the bytes. One file on the disk, one row in
 * the local files page, however many collections play it.
 *
 * "The same file" is decided by name, size and last-modified time — `refMatchesEntry`, the same
 * test the library already uses to recognise a moved file when it is dropped again. Not a hash:
 * hashing means reading every byte of every dropped file BEFORE anything appears, which is the one
 * thing the import path is built not to do (the rail shows the file at once; the copy and the
 * analysis follow in the background). The platform hands us those three facts for free, with no
 * read at all. What that costs is a pair of genuinely different files that share a name, a size
 * and a timestamp — on one machine, that is the same file — and a re-exported or renamed copy of
 * the same audio, which mints a second entry exactly as it does today. Neither loses data: the
 * entry keeps its own bytes either way, so there is no shared copy for two owners to argue over.
 *
 * Leaving a collection and leaving the device are therefore two different acts.
 * `removeLocalEntryFromScope` drops one membership; `removeLocalEntry` takes the bytes.
 *
 * A library imported under the OLD model still holds one file as several entries, each with its
 * own copy. The store's upgrade cannot fix that — turning two rows into one means deleting one of
 * somebody's entries — so `findLocalDuplicates` only finds them, and `mergeLocalDuplicates` acts
 * once a person looking at the list has said so. `localDuplicates.js` has the rules.
 *
 * ## What is never done here
 *
 * No upload, no copy into Cache Storage, no request to the platform carrying a filename. The copy
 * above is on the user's own machine and reachable by nothing else. A local id in telemetry is
 * redacted at the telemetry layer; a local id in a saved layout is stripped by the collection app;
 * a local id offered to a room is refused before it reaches the wire.
 */
import { Constants } from '../config/Constants.js';
import { resetDefaultPair, resolveDefaultPair } from '../config/defaultPair.js';
import { INLINE_SCOPE } from './localAudioConfig.js';
import { createLocalEntryId, isLocalEntryId } from './localAudioIds.js';
import { adoptionPatch, foldRailOrder, groupLocalDuplicates, localDuplicateKey } from './localDuplicates.js';
import { registerLocalByteSource, unregisterLocalByteSource } from './localByteSources.js';
import {
  deleteVaultCopy,
  ensurePersistentStorage,
  readVaultCover,
  sweepVaultOrphans,
  vaultUsageBytes,
  withVaultLock,
  writeVaultCopy,
  writeVaultCover,
} from './localByteVault.js';
import { prepareLocalCover } from './tags/localCovers.js';
import { TAG_READER_VERSION, readAudioTagsFromBlob } from './tags/readAudioTags.js';
import {
  ANALYSIS_STATE,
  deleteLocalEntry,
  isLocalStoreOpen,
  listLocalEntries,
  patchLocalEntry,
  readLocalEntry,
  readLocalRailArrangement,
  writeLocalRailPanel,
  readLocalScopeNames,
  writeLocalEntry,
  writeLocalRailArrangement,
  writeLocalScopeName,
} from './localLibraryStore.js';
import {
  checkEntryPresence,
  classifyFile,
  forgetSessionFile,
  persistedLocationOf,
  pickLocalFiles,
  pickLocalFolder,
  readEntryBytes,
  readRefBytes,
  refMatchesEntry,
  rememberSessionFile,
  requestEntryPermissions,
  localFilePlatform,
  localFilesAreCopied,
} from './localFileAccess.js';
import { cancelAnalysis, enqueueAnalysis, isAnalysisRunning, readJobState, readQueueState, subscribeToAnalysis } from './analysis/localAnalysisQueue.js';

/** A local entry's state on the rail. */
export const LOCAL_STATUS = {
  READY: 'ready',
  ANALYSING: 'analysing',
  PERMISSION: 'permission',
  MISSING: 'missing',
  FAILED: 'failed',
  /** Added from a folder, analysis deferred until the card is staged. */
  DEFERRED: 'deferred',
  /** Being copied into this browser. Plays as soon as it is analysed; the copy is what makes it last. */
  COPYING: 'copying',
  /** The copy could not be made. Playable now, gone after a reload — and the card says so. */
  SESSION_ONLY: 'session-only',
};

const listeners = new Set();
/** id → entry (the store's row, live) */
const entries = new Map();
/** id → 'ready' | 'permission' | 'missing' — the platform's answer, refreshed on demand */
const presence = new Map();
/** scope key → the collection's name when a file was last added to it */
const scopeNames = new Map();
/** id → blob: URL of the peaks JSON */
const peaksUrls = new Map();
/** id → blob: URL of the cover, or null for an entry asked about that turned out to have none. */
const coverUrls = new Map();
/** Ids whose cover is being read right now, so a rail of cards asks for each one once. */
const coverLoads = new Set();
/** id → 0..1 while this browser is taking its copy */
const copying = new Map();
/** id → why the copy failed ('quota' | 'write' | 'unsupported'), for the cards that say so */
const copyFailures = new Map();
/** Copies run one at a time: a folder's worth at once would fight the analysis for the same disk. */
let copyChain = Promise.resolve();
/** id → AbortController, so removing an entry can stop the copy that is already under way. */
const copyAborts = new Map();
/** Bytes the copies use, refreshed when one lands or one is deleted. */
let copiedBytes = 0;
let booted = null;
/** Whether this session has already gone looking for the tags of files imported before we read any. */
let backfilledTags = false;

/**
 * Drop a remembered cover URL, revoking it first.
 *
 * Every site that forgets one has to go through here: a `blob:` URL is a reference the document
 * holds until it is revoked, so deleting the map entry alone leaks the bytes for the life of the
 * page — and these are images, on the one surface that reports what this machine is holding.
 */
function forgetCoverUrl(id) {
  const url = coverUrls.get(id);
  if (url) URL.revokeObjectURL(url);
  coverUrls.delete(id);
}

function emit() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.warn('[local] library listener failed', error);
    }
  });
}

export function subscribeToLocalLibrary(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ── Synthetic release ────────────────────────────────────────────────────────────────────────────

/**
 * A cheap identity for a peaks document. The store hands back a fresh object on every read, so
 * object identity would say "changed" on every patch — and revoking the URL then would strand a
 * voice already booted from it (the waveform fetches on open, from the release it was given).
 * The URL is minted once per DOCUMENT and only replaced when a different analysis lands.
 */
function peaksFingerprint(peaks) {
  const data = Array.isArray(peaks?.data) ? peaks.data : [];
  return `${peaks?.sample_rate}:${peaks?.samples_per_pixel}:${peaks?.length}:${data[0]}:${data[1]}:${data[data.length - 2]}:${data[data.length - 1]}`;
}

function peaksUrlFor(entry) {
  if (!entry.peaks) return null;
  const fingerprint = peaksFingerprint(entry.peaks);
  const existing = peaksUrls.get(entry.id);
  if (existing?.fingerprint === fingerprint) return existing.url;
  if (existing?.url) URL.revokeObjectURL(existing.url);
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function' || typeof Blob === 'undefined') return null;
  const url = URL.createObjectURL(new Blob([JSON.stringify(entry.peaks)], { type: 'application/json' }));
  peaksUrls.set(entry.id, { fingerprint, url });
  return url;
}

/**
 * The release payload the engine boots this entry from. Exported for the tests, which pin the
 * fields the adapter, the waveform and the assembler read.
 */
export function buildLocalTrackRelease(entry, defaults) {
  const orbiterId = entry.orbiterId || defaults.orbiterId;
  const worldId = entry.entangledWorldId || defaults.worldId;
  const orbiterRelease = entry.orbiterId && entry.orbiterId !== defaults.orbiterId ? null : defaults.orbiterRelease;
  const worldRelease = entry.entangledWorldId && entry.entangledWorldId !== defaults.worldId ? null : defaults.worldRelease;
  const loudness = entry.loudness || {};
  return {
    success: true,
    trackId: entry.id,
    currentReleaseVersion: 1,
    isLatest: true,
    release: {
      version: 1,
      status: 'local',
      metadata: {
        trackId: entry.id,
        // The title the file gives itself, and the file name when it gives none. A card next to
        // one of ours should read like one; the file name is still what the local files page and
        // the person's own folder call it.
        trackName: entry.tags?.title || entry.name,
        // The shape a hosted release uses, and the one `pickDisplayName` reads: the info panel and
        // the OS media session get the artist from here. The rail CARD does not — it is built from
        // the entry directly (`toLocalDrawerEntry`), which reads the same tags.
        artists: entry.tags?.artist ? [{ displayName: entry.tags.artist }] : [],
        localAudio: true,
        defaultOrbiterId: orbiterId,
        defaultOrbiterVersion: entry.orbiterVersion || defaults.orbiterVersion || null,
        defaultEntangledWorldId: worldId,
        defaultEntangledWorldVersion: entry.entangledWorldVersion || defaults.worldVersion || null,
        // The backend's own analysis shape. `duration_sec` sits at the top; the detected tempo goes
        // under `analysis`, which is exactly where the shared resolver looks for it — so a local
        // entry reaches the sync engine down the identical path a hosted release does, with no
        // second code path and nothing new in the deck. A tempo the analysis would not vouch for is
        // simply absent, which the resolver already reads as "this track has no grid".
        audioAnalysis: {
          ...(entry.durationSec ? { duration_sec: entry.durationSec } : {}),
          ...(entry.tempoBpm ? { analysis: { tempo_bpm: entry.tempoBpm } } : {}),
        },
        audioNormalization: {
          integratedLufs: loudness.integratedLufs ?? null,
          truePeakDb: loudness.truePeakDb ?? null,
          playbackGainDb: loudness.playbackGainDb ?? null,
          maxAllowableGainDb: loudness.maxAllowableGainDb ?? null,
        },
      },
      assets: {
        hasCompressed: true,
        hasLossless: false,
        waveformURL: peaksUrlFor(entry),
      },
    },
    hydration: {
      orbiterId,
      orbiterVersion: orbiterRelease?.version ?? null,
      orbiterRelease,
      worldId,
      worldVersion: worldRelease?.version ?? null,
      worldRelease,
    },
  };
}

/**
 * Apply a change to an entry: in the store when it answers, and in memory regardless. A session
 * whose IndexedDB is denied still plays what was dropped into it; it just forgets on reload.
 */
async function applyEntryPatch(id, patch) {
  const current = entries.get(id);
  if (!current) return null;
  const stored = await patchLocalEntry(id, patch);
  const next = stored ?? { ...current, ...patch };
  entries.set(id, next);
  return next;
}

/** Called when an entry's loudness lands — the collection app updates any voice already playing it. */
let loudnessListener = null;
export function setLocalLoudnessListener(listener) {
  loudnessListener = typeof listener === 'function' ? listener : null;
}

/**
 * Called when an entry's tempo lands, for the same reason the loudness one exists.
 *
 * Peaks make a card stageable seconds before the tempo pass finishes, so a file CAN be staged and
 * playing while it still has no grid. Re-priming the release only helps the next voice to boot;
 * this hands the tempo to the one already playing.
 */
let tempoListener = null;
export function setLocalTempoListener(listener) {
  tempoListener = typeof listener === 'function' ? listener : null;
}

async function primeEntry(entry) {
  const defaults = await resolveDefaultPair();
  const payload = buildLocalTrackRelease(entry, defaults);
  Constants.setTrackRelease(entry.id, payload, { version: null });
  Constants.setTrackRelease(entry.id, payload, { version: 1 });
  // A previously assembled session for this id is stale once the release changed (new peaks, new gain).
  Constants.dropConfigsForTrack(entry.id);
  registerLocalByteSource(entry.id, () => readEntryBytes(entries.get(entry.id) ?? entry));
}

// ── The copy this browser keeps ──────────────────────────────────────────────────────────────────

/**
 * Take this browser's copy of a just-imported file, in the background and one at a time.
 *
 * The entry is already in the rail and already playable from the dropped `File`; the copy is what
 * makes it still be there tomorrow. It runs after the entry is stored on purpose — an interrupted
 * copy then leaves an entry that says "add it again", which is recoverable, rather than bytes with
 * nothing pointing at them, which is litter (the boot sweep catches those too).
 */
function queueVaultCopy(entry, ref) {
  copying.set(entry.id, 0);
  copyFailures.delete(entry.id);
  const abort = new AbortController();
  copyAborts.set(entry.id, abort);
  emit();
  let lastPercent = -1;
  copyChain = copyChain.then(async () => {
    // Removed while it waited its turn: do not read the file, do not write anything back.
    if (abort.signal.aborted || !entries.has(entry.id)) {
      copying.delete(entry.id);
      copyAborts.delete(entry.id);
      return;
    }
    try {
      const blob = await readRefBytes(ref);
      await withVaultLock(() => writeVaultCopy(entry.id, blob, {
        signal: abort.signal,
        onProgress: (fraction) => {
          const percent = Math.round(fraction * 100);
          // The rail redraws on every emit; a byte-by-byte one would redraw it thousands of times.
          if (percent === lastPercent) return;
          lastPercent = percent;
          copying.set(entry.id, fraction);
          emit();
        },
      }));
      // Removed while the bytes were being written: the entry is gone, so the copy has no owner.
      // Take it back rather than leaving litter for the next boot to find.
      if (abort.signal.aborted || !entries.has(entry.id)) {
        await deleteVaultCopy(entry.id);
        return;
      }
      const next = await applyEntryPatch(entry.id, { copied: true });
      // The copy is the source of truth from here: drop the session's hold on the original so the
      // very next read proves the copy works, rather than finding out after a reload that it does not.
      forgetSessionFile(entry.id);
      if (next) await primeEntry(next);
      presence.set(entry.id, 'ready');
      // Measured, not accumulated: a number added up from intentions drifts from the disk it claims
      // to describe (a delete that failed, another tab, a replaced copy).
      copiedBytes = await vaultUsageBytes();
    } catch (error) {
      if (error?.code === 'cancelled') return;
      // A file that could not be copied still plays this session — the card says it will not last.
      copyFailures.set(entry.id, error?.code || 'write');
      console.warn('[local] this browser could not keep a copy', entry.name, error);
    } finally {
      copying.delete(entry.id);
      copyAborts.delete(entry.id);
      emit();
    }
  });
  return copyChain;
}

/**
 * Resolves when every copy asked for so far has landed or failed. The copies are deliberately a
 * background job — the rail must not wait on the disk — so anything that needs to act *after* them
 * (a batch import reporting what it kept, a test) has to be able to ask.
 */
export function whenLocalCopiesSettled() {
  return copyChain;
}

/** Why the copy of `id` failed, or null. */
export function localCopyFailureOf(id) {
  return copyFailures.get(id) ?? null;
}

/** 0..100 while this browser is taking its copy, else null. */
export function localCopyPercent(id) {
  const fraction = copying.get(id);
  return fraction === undefined ? null : Math.round(fraction * 100);
}

/**
 * Ask the disk again what the copies are using, and publish the answer.
 *
 * Measured, never accumulated — a running total drifts from the disk it claims to describe the
 * first time a delete fails or another tab imports. Any surface that states a number to the person
 * whose machine it is should call this rather than trusting the last one it saw.
 */
export async function refreshLocalCopiedBytes() {
  copiedBytes = await vaultUsageBytes();
  emit();
  return copiedBytes;
}

/** Whether files imported here are copied into this browser at all. */
export { localFilesAreCopied };

// ── Boot ─────────────────────────────────────────────────────────────────────────────────────────

/** Load the library from the store and register every entry with the engine. Idempotent. */
export function bootLocalLibrary() {
  if (booted) return booted;
  booted = (async () => {
    // The fallback pair a download kept, before anything asks for one. A file on this machine has no
    // pair of its own, so this IS what it plays with — and with no connection the chain that would
    // otherwise find it ends at the in-app constants and a request for them that cannot succeed.
    // Harmless online: the live settings/platform resolution is still attempted first.
    // Dynamic, to keep the offline library out of this module's static graph.
    await import('../offline/offlineDefaultPair.js')
      .then(({ primeStoredDefaultPair }) => primeStoredDefaultPair())
      .catch(() => null);
    const rows = await listLocalEntries();
    Object.entries(await readLocalScopeNames()).forEach(([key, name]) => scopeNames.set(key, name));
    // An open that did not answer is not an empty library: forget this boot so the next session
    // surface (the next collection opened) asks again.
    if (!isLocalStoreOpen()) booted = null;
    for (const row of rows) {
      if (entries.has(row.id)) continue;
      entries.set(row.id, row);
      await primeEntry(row);
    }
    emit();
    await refreshPresence();
    // Sweeping deletes files, so it is fenced twice.
    //
    // First: only against a library the store really told us about. An IndexedDB open that failed
    // leaves `entries` empty, and sweeping on that would delete every copy the user has.
    //
    // Second: under the vault lock, comparing against the store RE-READ inside it — never this
    // tab's snapshot. Another tab may have imported since we listed, and its copy is not an orphan.
    // The invariant that makes this sound is that an entry is written to the store BEFORE its copy
    // is queued, so a copy that exists always has a row someone can see.
    if (isLocalStoreOpen()) {
      await withVaultLock(async () => {
        const live = await listLocalEntries();
        if (!isLocalStoreOpen()) return;
        await sweepVaultOrphans(live.map((row) => row.id));
        copiedBytes = await vaultUsageBytes();
      });
      emit();
    }
    // Detached on purpose: a library that already exists should fill in its artists and covers
    // without anything waiting for it to finish.
    void backfillTags();
  })();
  if (!analysisSubscribed) {
    analysisSubscribed = true;
    subscribeToAnalysis(() => {
      // "N of M" counts a run: what finished since the queue was last idle. Asked as a yes/no, not
      // read off a whole-queue picture — this runs on every step of every job of every import.
      if (!isAnalysisRunning()) finishedThisRun.clear();
      emit();
    });
  }
  return booted;
}
let analysisSubscribed = false;
const finishedThisRun = new Set();

/** Entries of a scope left mid-analysis by a closed tab pick up where the store says they stopped. */
export function resumePendingAnalysis(scope = null) {
  listLocalLibrary(scope)
    .filter((row) => row.analysis === ANALYSIS_STATE.PENDING || row.analysis === ANALYSIS_STATE.COARSE)
    .forEach((row) => {
      if (presence.get(row.id) === 'ready' && !readJobState(row.id)) queueAnalysis(row.id);
    });
}

/** Ask the platform which entries can be reached, and publish the answer. */
export async function refreshPresence(ids = null) {
  const targets = ids ? ids.map((id) => entries.get(id)).filter(Boolean) : [...entries.values()];
  await Promise.all(
    targets.map(async (entry) => {
      presence.set(entry.id, await checkEntryPresence(entry));
    }),
  );
  emit();
}

// ── Reading ──────────────────────────────────────────────────────────────────────────────────────

/** The collections an entry belongs to, always an array. */
export function scopesOf(entry) {
  return Array.isArray(entry?.scopes) ? entry.scopes : [];
}

/**
 * The entries of one scope — a collection, or the inline session. A file dropped while playing one
 * collection belongs to that collection: the library is one store on this machine, but each rail
 * shows only what was added to it. The same file may be in several, and then it is in all of them.
 *
 * `null` asks for the entries that belong to no collection at all — the ones a deleted collection
 * left behind, and the only place they can be reached.
 */
export function listLocalLibrary(scope = null) {
  if (scope === null) return [...entries.values()].filter((entry) => scopesOf(entry).length === 0);
  return [...entries.values()].filter((entry) => scopesOf(entry).includes(scope));
}

/** Every entry on this machine, whichever collections it belongs to. */
export function listAllLocalEntries() {
  return [...entries.values()];
}

/**
 * Every collection this library holds files for, with what it is holding for each — the local
 * files page's folder view, and the only surface that can reach a collection that has been deleted.
 *
 * The `null` key is the "no collection" bucket. Names are the last ones seen at import; a
 * collection whose name was never recorded is listed under its key with a null name, which the
 * page words rather than inventing one here.
 */
export function listLocalScopes() {
  const buckets = new Map();
  for (const entry of entries.values()) {
    const keys = scopesOf(entry);
    for (const key of keys.length ? keys : [null]) {
      const bucket = buckets.get(key) ?? { key, name: key === null ? null : (scopeNames.get(key) ?? null), entries: [], bytes: 0 };
      bucket.entries.push(entry);
      bucket.bytes += Number(entry.size) || 0;
      buckets.set(key, bucket);
    }
  }
  return [...buckets.values()];
}

/** The name a collection had the last time a file was added to it, or null. */
export function localScopeName(scope) {
  return scope === null ? null : (scopeNames.get(scope) ?? null);
}

/** Scope keys this session asked the platform about and could not name, and why. */
const scopeNameFailures = new Map();

/**
 * Why a collection could not be named. Four different facts, because they are four different
 * things to say to somebody looking for their files:
 *
 *   `gone`         the platform says it no longer exists (404). Those files are stranded, and
 *                  this page is the only place left that can reach them.
 *   `no-access`    it answered, and refused this viewer (401/403). The collection may be perfectly
 *                  alive and belong to someone else — which is not the same as deleted, and not
 *                  the same as a broken connection.
 *   `sign-in`      the platform will not answer at all without an account.
 *   `unreachable`  nothing answered. No verdict about the collection, only about the connection.
 *   `unnamed`      it answered, and has no name. Nothing is wrong with anything.
 *
 * Everything used to collapse into `unreachable`, which said "this browser cannot reach it" about
 * a collection the browser reached perfectly well and was told to keep out of.
 */
export function localScopeNameFailure(scope) {
  return scopeNameFailures.get(scope) ?? null;
}

/** The platform's answer, as a reason this collection has no name. */
function scopeFailureFor(error) {
  switch (error?.code) {
    case 'not-found':
      return 'gone';
    case 'no-access':
      return 'no-access';
    case 'sign-in-required':
      return 'sign-in';
    default:
      return 'unreachable';
  }
}

/**
 * Give every collection this library holds files for a NAME.
 *
 * A file imported before the library started remembering names — or one added by a build that did
 * not — leaves only the collection's id, and an id is not something to show a person. So the ones
 * that are still unnamed are looked up once, and the answer is written into the store: the cost is
 * one request per collection ever, not per visit, and after it the page can name that collection
 * with no connection at all.
 *
 * A collection that has been DELETED answers 404, and that is worth knowing rather than retrying —
 * it is the case this whole page exists for, and the label can say so. Every other answer, refusal
 * included, is remembered only for this session: access is given and taken away, and a permission
 * written down would outlive the fact it recorded.
 *
 * The collection loader is injected rather than imported: this module is the library, and the
 * library must not decide that naming a collection is worth a network request in every context
 * that boots it. The page decides that.
 *
 * Stops the moment `signal` aborts: the surface that asked has gone, and a page nobody is looking
 * at should not keep spending requests on it.
 *
 * @param {{ fetchCollection: (id: string) => Promise<object|null>, signal?: AbortSignal }} deps
 */
export async function resolveLocalScopeNames({ fetchCollection, signal }) {
  if (typeof fetchCollection !== 'function') return;
  const unknown = listLocalScopes()
    .map((bucket) => bucket.key)
    // Never the inline session: that key is ours, minted for a file dropped outside any
    // collection, and asking the platform to name it is a request that can only ever 404.
    .filter((key) => key && key !== INLINE_SCOPE && !scopeNames.has(key) && !scopeNameFailures.has(key));
  if (!unknown.length) return;
  for (const key of unknown) {
    if (signal?.aborted) return;
    try {
      const collection = /** @type {{ name?: unknown }|null} */ (await fetchCollection(key));
      if (signal?.aborted) return;
      const name = typeof collection?.name === 'string' ? collection.name.trim() : '';
      if (name) {
        scopeNames.set(key, name);
        await writeLocalScopeName(key, name);
      } else {
        // It answered, and it has no name to give. Nothing is wrong with the connection or with
        // this viewer, so neither of those is what the label should say.
        scopeNameFailures.set(key, 'unnamed');
      }
    } catch (error) {
      scopeNameFailures.set(key, scopeFailureFor(error));
    }
  }
  emit();
}

/**
 * The cover this browser kept for an entry, as a `blob:` URL — or null, which is also the answer
 * while one is being read.
 *
 * Asked for by the cards on a rail as they render, not loaded at boot: a library of five hundred
 * files should not open five hundred images to draw the dozen tiles on screen. The first call for
 * an entry starts the read and returns null; when it lands the library emits and the card that
 * asked draws it. An entry that turns out to have no cover is remembered as such and never
 * re-opened.
 */
export function localCoverUrl(id) {
  if (coverUrls.has(id)) return coverUrls.get(id);
  const entry = entries.get(id);
  if (!entry?.coverStored || coverLoads.has(id)) return null;
  coverLoads.add(id);
  void (async () => {
    const file = await readVaultCover(id);
    coverLoads.delete(id);
    // Removed while its cover was being read: nothing should hold a URL to it.
    if (!entries.has(id)) return;
    coverUrls.set(id, file ? URL.createObjectURL(file) : null);
    if (file) emit();
  })();
  return null;
}

export function readLocalEntryById(id) {
  return entries.get(id) ?? null;
}

/** The state a card shows. */
export function localStatusOf(id) {
  const entry = entries.get(id);
  if (!entry) return null;
  const reach = presence.get(id) ?? 'ready';
  if (reach === 'missing') return LOCAL_STATUS.MISSING;
  if (reach === 'permission') return LOCAL_STATUS.PERMISSION;
  if (entry.analysis === ANALYSIS_STATE.FAILED) return LOCAL_STATUS.FAILED;
  // Copying is reported ahead of analysing: both are running, and the copy is the one the person
  // has not seen before and is the reason the file will still be here tomorrow.
  if (copying.has(id)) return LOCAL_STATUS.COPYING;
  // Playable, but this browser could not keep it. Said plainly now, rather than discovered as a
  // missing card after the next reload.
  if (copyFailures.has(id)) return LOCAL_STATUS.SESSION_ONLY;
  if (entry.analysis === 'deferred') return LOCAL_STATUS.DEFERRED;
  const job = readJobState(id);
  if (job && job.phase !== 'done') return LOCAL_STATUS.ANALYSING;
  if (entry.analysis === ANALYSIS_STATE.PENDING) return LOCAL_STATUS.ANALYSING;
  return LOCAL_STATUS.READY;
}

/** 0..100 while a card is being analysed, else null. */
export function localAnalysisPercent(id) {
  const job = readJobState(id);
  if (!job) return null;
  if (job.phase === 'queued' || job.phase === 'decoding') return 0;
  // Peaks are the first third of the visible bar — the card is stageable at a third, which is what
  // the design's "38 %" is showing. Tempo takes a short slice after it and loudness, much the
  // longest pass in wall-clock terms, has the rest.
  if (job.phase === 'peaks') return Math.round(job.fraction * 33);
  if (job.phase === 'tempo') return 33 + Math.round(job.fraction * 7);
  if (job.phase === 'loudness') return 40 + Math.round(job.fraction * 60);
  return job.phase === 'done' ? 100 : null;
}

/** Whether the engine can play this entry now (peaks in hand, file reachable). */
export function isLocalEntryStageable(id) {
  const status = localStatusOf(id);
  const entry = entries.get(id);
  if (!entry) return false;
  if (status === LOCAL_STATUS.MISSING || status === LOCAL_STATUS.PERMISSION || status === LOCAL_STATUS.FAILED) return false;
  // A copy in flight does not hold the card back: the original is still open, and waiting for the
  // disk before letting someone play what they just dropped would be a delay for no reason.
  return Boolean(entry.peaks) || status === LOCAL_STATUS.DEFERRED;
}

/**
 * The header's counters.
 *
 * ONE pass over the scope, not one per counter. Every count here used to re-derive each entry's
 * status from scratch, and this runs on every redraw of a rail that may hold several hundred
 * cards — the arithmetic of counting four things four times is what made importing a folder a wait.
 */
export function readLocalLibrarySummary(scope = null) {
  const mine = listLocalLibrary(scope);
  const inScope = new Set(mine.map((entry) => entry.id));
  let missing = 0;
  let needPermission = 0;
  let sessionOnly = 0;
  let copyingHere = 0;
  let queuedHere = 0;
  for (const entry of mine) {
    const status = localStatusOf(entry.id);
    if (status === LOCAL_STATUS.MISSING) missing += 1;
    else if (status === LOCAL_STATUS.PERMISSION) needPermission += 1;
    else if (status === LOCAL_STATUS.SESSION_ONLY) sessionOnly += 1;
    if (copying.has(entry.id)) copyingHere += 1;
    if (readJobState(entry.id)) queuedHere += 1;
  }
  let doneHere = 0;
  finishedThisRun.forEach((id) => {
    if (inScope.has(id)) doneHere += 1;
  });
  const activeId = readQueueState().active?.id ?? null;
  return {
    total: mine.length,
    missing,
    needPermission,
    // Progress of THIS scope's files: the queue may also be working for another collection.
    analysing: queuedHere ? { done: doneHere, total: queuedHere + doneHere, activeId: activeId && inScope.has(activeId) ? activeId : null } : null,
    platform: localFilePlatform(),
    // What this browser is holding, so the rail can say it without anyone having to ask.
    copying: copyingHere,
    sessionOnly,
    copies: localFilesAreCopied(),
    copiedBytes,
  };
}

// ── Import ───────────────────────────────────────────────────────────────────────────────────────

/**
 * Read the tags of files that were imported before anything read tags.
 *
 * Without this the artist column and the cover would only ever appear for files added from now on,
 * and every library that already exists would show a dash for ever — which is the state that was
 * worth fixing in the first place.
 *
 * Deliberately quiet and deliberately cheap:
 *
 * - only files this browser holds its own copy of, which it can open without asking anything and
 *   read a slice of without reading the rest;
 * - only as much of each file as its own tag says it needs, sliced rather than read, so this
 *   costs kilobytes and not the library's worth of audio;
 * - one at a time, after boot rather than inside it, so nothing waits for it;
 * - and the answer is written even when the file says nothing, stamped with the version of the
 *   reader that produced it, so a file with no tags stops being opened on every boot — while a
 *   file the NEXT reader could do better with is opened exactly once more.
 */
async function backfillTags() {
  if (backfilledTags) return;
  backfilledTags = true;
  let found = 0;
  for (const row of [...entries.values()]) {
    const entry = entries.get(row.id);
    // Read by THIS reader, not merely read: a row whose answer came from an older one is asked
    // again, because "this file says nothing" is exactly the answer a better reader overturns.
    if (!entry || entry.tagsVersion === TAG_READER_VERSION || presence.get(entry.id) === 'missing') continue;
    // Only files this browser holds its OWN copy of. Two reasons, and both are the contract above:
    // a handle would raise a permission prompt nobody asked for, and the desktop shell's bridge
    // has no ranged read — `readEntryBytes` there hands back the WHOLE file, so a library of
    // takes would be read end to end at boot to look at the first few kilobytes of each. A copy
    // in the private file system slices for free. (Desktop keeps its file names until a ranged
    // read exists on the bridge; nothing is lost, and nothing is read that need not be.)
    if (!entry.copied) continue;
    // Already on its way through the analysis, which reads the tags itself.
    if (readJobState(entry.id)) continue;
    try {
      const bytes = await readEntryBytes(entry);
      // Asks the file how long its own metadata is and reads exactly that — a fixed guess would
      // truncate the tags of a file carrying a cover and read a megabyte for one carrying none.
      const tags = await readAudioTagsFromBlob(bytes, { picture: true });
      const cover = await prepareLocalCover(tags.picture);
      const kept = cover ? await withVaultLock(() => writeVaultCover(entry.id, cover)) : false;
      const next = await applyEntryPatch(entry.id, {
        tags: { title: tags.title, artist: tags.artist, album: tags.album },
        tagsVersion: TAG_READER_VERSION,
        ...(kept ? { coverStored: true } : {}),
      });
      if (kept) forgetCoverUrl(entry.id);
      if (next && (tags.title || tags.artist || kept)) found += 1;
    } catch {
      // Not readable right now. The field stays unset, so the next boot looks again.
    }
  }
  if (found) {
    copiedBytes = await vaultUsageBytes();
    emit();
  }
}

function queueAnalysis(id) {
  const entry = entries.get(id);
  if (!entry) return;
  enqueueAnalysis({
    id,
    getBytes: () => readEntryBytes(entries.get(id) ?? entry),
    // What the file calls itself. Stored beside the name rather than instead of it: the file name
    // is how a person finds the file on their own disk, and it is what the local files page lists.
    // The CARD is where the tags win, because a card is about the music.
    onTags: async ({ title, artist, album, picture }) => {
      // Made small and copied out of the file's buffer BEFORE this returns — that buffer is the
      // one the decode is about to detach, and a cover held as a view into it would be empty.
      const cover = await prepareLocalCover(picture);
      // Under the vault lock, like every other write into it: the cover shares the quota the
      // audio copies and the offline collections are all spending.
      const kept = cover ? await withVaultLock(() => writeVaultCover(id, cover)) : false;
      const next = await applyEntryPatch(id, {
        tags: { title, artist, album },
        tagsVersion: TAG_READER_VERSION,
        ...(kept ? { coverStored: true } : {}),
      });
      if (!next) return;
      if (kept) {
        forgetCoverUrl(id);
        copiedBytes = await vaultUsageBytes();
      }
      await primeEntry(next);
      emit();
    },
    onCoarse: async ({ peaks, durationSec }) => {
      const next = await applyEntryPatch(id, { peaks, durationSec, analysis: ANALYSIS_STATE.COARSE });
      if (!next) return;
      await primeEntry(next);
      emit();
    },
    onTempo: async ({ tempoBpm }) => {
      // Stored either way. `null` is this file's answer — it has no pulse steady enough to warp to —
      // and storing it keeps a re-analysis from being the only way to tell that from "not asked yet".
      const next = await applyEntryPatch(id, { tempoBpm: tempoBpm ?? null });
      if (!next) return;
      await primeEntry(next);
      // Only a tempo is handed to a voice already playing, never a retraction. A re-analysis that
      // comes back with no tempo leaves that voice warping to the grid it started on, deliberately:
      // the deck has no way to UNSET a native tempo (`setNativeTempo` ignores a null), and pulling
      // the grid out from under a playing track would be a worse answer than letting it finish on
      // the one it began with. The next voice booted from this entry reads the retraction.
      if (next.tempoBpm) tempoListener?.(id, next.tempoBpm);
      emit();
    },
    onDone: async ({ loudness, durationSec }) => {
      finishedThisRun.add(id);
      const next = await applyEntryPatch(id, { loudness, durationSec, analysis: ANALYSIS_STATE.READY });
      if (!next) return;
      await primeEntry(next);
      // A voice booted from the coarse pass is already playing at 0 dB: hand it the gain now.
      loudnessListener?.(id, next.loudness);
      emit();
    },
    onError: async (error) => {
      console.warn('[local] analysis failed', entry.name, error);
      const code = error?.code;
      // A failure only condemns an entry that had nothing to lose. A RE-analysis that fails leaves
      // the previous reading standing — the peaks, gain and tempo in hand are still this file's, and
      // FAILED would make a card that is playing correctly unstageable. But it has to be put BACK,
      // not merely spared: the re-analysis moved the row to PENDING and the job that would have
      // moved it on is gone, so left alone the card reads "analysing" for ever, with no percentage
      // and neither analysis action offered.
      const current = entries.get(id);
      const complete = Boolean(current?.peaks && current?.loudness);
      if (code === 'missing' || code === 'permission') {
        // Presence explains this card; the row's own state is only touched when it would otherwise
        // be stranded mid-flight. A first analysis that never landed stays PENDING on purpose —
        // that is what makes the next boot pick it up again.
        presence.set(id, code);
        if (complete && current?.analysis === ANALYSIS_STATE.PENDING) {
          await applyEntryPatch(id, { analysis: ANALYSIS_STATE.READY });
        }
      } else {
        await applyEntryPatch(id, { analysis: complete ? ANALYSIS_STATE.READY : ANALYSIS_STATE.FAILED });
      }
      emit();
    },
  });
}

/**
 * Add files to the library.
 *
 * Unsupported files are counted and reported, never confirmed: the ones that work are already in
 * the rail, and every one of them is queued for analysis straight away — one file or five hundred,
 * there is no second path and nothing to ask.
 *
 * A dropped file the library already holds does not become a second entry. Which of three things
 * it becomes depends on where that entry stands (see `refMatchesEntry` and the header's note on
 * identity):
 *
 *   `reattached`  the entry was MISSING — the drop is the designed way to fix a moved file, so it
 *                 takes the new location, and joins this collection too.
 *   `linked`      the entry is here and in some other collection — it joins this one as well. No
 *                 second copy of the bytes, no second row anywhere.
 *   `alreadyHere` the entry is already in this very collection. Nothing to do, and saying so is
 *                 better than adding a card that was already on the rail.
 *
 * @param {import('./localFileAccess.js').FileRef[]} refs
 * @param {{ scope?: string|null, scopeName?: string|null }} [options]
 * @returns {Promise<{ added: object[], reattached: object[], linked: object[], alreadyHere: object[], skipped: Array<{name: string, reason: string}> }>}
 */
export async function importLocalFiles(refs, { scope = null, scopeName = null } = {}) {
  await bootLocalLibrary();
  const added = [];
  const reattached = [];
  const linked = [];
  const alreadyHere = [];
  const skipped = [];
  const copyRefs = [];
  const copies = localFilesAreCopied();
  // Asked once, on the first import that will be kept: Safari clears storage that was never
  // claimed, and it would take the library's copies with it.
  if (copies) void ensurePersistentStorage();
  if (scope && scopeName) {
    scopeNames.set(scope, scopeName);
    void writeLocalScopeName(scope, scopeName);
  }
  for (const ref of refs || []) {
    const verdict = classifyFile(ref);
    if (verdict !== 'ok') {
      skipped.push({ name: ref.name, reason: verdict });
      continue;
    }
    // Across the WHOLE library, not this collection: the entry a drop matches may well be one
    // another collection put here, and that is exactly the copy this avoids making twice.
    //
    // Read LIVE, every time, rather than indexed once before the loop. This loop awaits — the store
    // write, the prime, the scope patch — and entries appear and are REPLACED under it: by the rest
    // of this same drop, and by a second import the person started while this one was still
    // landing. An index built up front misses both, and what it misses becomes a second row and a
    // second copy of the same bytes. The scan is a few hundred cheap comparisons; the drop's real
    // cost is the disk, not this.
    const held = [...entries.values()].find((entry) => refMatchesEntry(ref, entry));
    if (held) {
      const wasHere = scopesOf(held).includes(scope);
      // Not just MISSING. A remembered handle whose permission has lapsed reads as `permission`,
      // and a copy that failed leaves an entry with nothing but that handle — dropping the file
      // again is the designed way back for both, and the drop is carrying a live `File` that
      // needs no permission at all. Recognising the entry and then not taking the file would
      // leave the card dimmed and say it was already here.
      const unreachable = presence.get(held.id) === 'missing' || presence.get(held.id) === 'permission';
      if (unreachable) await attachRefToEntry(held.id, ref);
      const next = (await addScopeToEntry(held.id, scope)) ?? entries.get(held.id);
      if (unreachable) reattached.push(next);
      else if (wasHere) alreadyHere.push(next);
      else linked.push(next);
      continue;
    }
    const entry = {
      id: createLocalEntryId(),
      name: ref.name,
      size: ref.size,
      lastModified: ref.lastModified ?? null,
      mimeType: ref.mimeType ?? null,
      durationSec: null,
      peaks: null,
      loudness: null,
      tempoBpm: null,
      analysis: ANALYSIS_STATE.PENDING,
      ...persistedLocationOf(ref),
      scopes: scope ? [scope] : [],
      orbiterId: null,
      orbiterVersion: null,
      entangledWorldId: null,
      entangledWorldVersion: null,
      addedAt: Date.now(),
    };
    // The dropped File is held until this browser's own copy has landed — it is what plays, and
    // what is analysed, in the seconds before the copy exists.
    if (ref.file) rememberSessionFile(entry.id, ref.file);
    // A store that will not write still gets a playable session: the entry lives in memory.
    await writeLocalEntry(entry);
    entries.set(entry.id, entry);
    presence.set(entry.id, 'ready');
    await primeEntry(entry);
    if (copies) copyRefs.push([entry, ref]);
    added.push(entry);
  }
  // Copying starts after every card is in the rail, so a folder appears at once instead of one
  // file at a time behind the disk.
  copyRefs.forEach(([entry, ref]) => queueVaultCopy(entry, ref));
  // Every file, always, whatever the size of the drop — there is no question to ask and no second
  // way for this to go. The peaks and the loudness passes are computed in a worker, so the run does
  // not compete with the music; the changeover between two files does, briefly, because opening and
  // decoding one happens on this thread. The queue runs one file at a time, so a folder of five
  // hundred costs no more memory at once than a folder of five.
  added.forEach((entry) => queueAnalysis(entry.id));
  emit();
  return { added, reattached, linked, alreadyHere, skipped };
}

/**
 * Run the analysis again on an entry that already has one.
 *
 * Deliberately separate from `ensureLocalEntryAnalysed`, which exists to make sure a file is
 * analysed BEFORE it plays and so returns early the moment it finds peaks. This is the opposite
 * intent — the results are there and we want new ones — and a flag on that function would have made
 * one call site mean two things.
 *
 * The old peaks, gain and tempo are kept until the new ones land, rather than cleared up front: a
 * card being re-analysed can still be playing, and taking its waveform away mid-flight would make a
 * working stage look broken. Every pass overwrites its own field as it finishes.
 */
export async function reanalyseLocalEntry(id) {
  const entry = entries.get(id);
  if (!entry) return;
  if (readQueueState().jobs[id]) return;
  // Awaited, not fired off: the job is queued only once the row actually says PENDING. Queue first
  // and the entry is briefly READY with a job running against it, so anything that reads the two
  // together in that window — the card's status, a stop sweep deciding what to put back — sees a
  // state that never really existed.
  await applyEntryPatch(id, { analysis: ANALYSIS_STATE.PENDING });
  queueAnalysis(id);
  emit();
}

/**
 * Make sure an entry is analysed before it plays.
 *
 * Everything dropped is analysed on import now, so this is for the two rows that are not: one whose
 * analysis failed, and one left `deferred` by a library imported when a folder-sized drop still
 * asked whether to analyse now or later.
 */
export function ensureLocalEntryAnalysed(id) {
  const entry = entries.get(id);
  if (!entry || entry.peaks) return;
  if (readJobState(id)) return;
  if (entry.analysis === 'deferred' || entry.analysis === ANALYSIS_STATE.FAILED) {
    void applyEntryPatch(id, { analysis: ANALYSIS_STATE.PENDING });
  }
  queueAnalysis(id);
  emit();
}

// ── Missing files ────────────────────────────────────────────────────────────────────────────────

async function attachRefToEntry(id, ref) {
  const entry = entries.get(id);
  if (!entry) return null;
  const sameFile = refMatchesEntry(ref, entry);
  const patch = {
    ...persistedLocationOf(ref),
    name: ref.name,
    size: ref.size,
    lastModified: ref.lastModified ?? entry.lastModified,
    mimeType: ref.mimeType ?? entry.mimeType,
    // A different file under the same card: its analysis is not this file's.
    ...(sameFile ? {} : { peaks: null, loudness: null, tempoBpm: null, durationSec: null, analysis: ANALYSIS_STATE.PENDING }),
  };
  if (ref.file && ref.kind === 'file') rememberSessionFile(id, ref.file);
  else forgetSessionFile(id);
  const next = await applyEntryPatch(id, patch);
  if (!next) return null;
  presence.set(id, 'ready');
  await primeEntry(next);
  if (!sameFile || !next.peaks) queueAnalysis(id);
  emit();
  return next;
}

/** "Locate this file…": the picker, then the chosen file re-attaches (re-analysed if it differs). */
export async function locateLocalEntry(id) {
  const entry = entries.get(id);
  if (!entry) return false;
  const picked = await pickLocalFiles();
  if (!picked.length) return false;
  const match = picked.find((ref) => refMatchesEntry(ref, entry)) ?? picked[0];
  await attachRefToEntry(id, match);
  return true;
}

/**
 * "Search a folder…": one picker, every missing entry that lives in it re-attached in one pass.
 * @returns {Promise<{ found: number, searched: number }|null>} null when the picker was cancelled.
 */
export async function searchFolderForMissing(scope = null) {
  const folder = await pickLocalFolder();
  if (!folder) return null;
  const missing = listLocalLibrary(scope).filter((entry) => localStatusOf(entry.id) === LOCAL_STATUS.MISSING);
  let found = 0;
  for (const entry of missing) {
    const ref = folder.files.find((candidate) => refMatchesEntry(candidate, entry));
    if (!ref) continue;
    await attachRefToEntry(entry.id, { ...ref, folder: ref.folder ?? folder.name, folderHandle: ref.folderHandle ?? folder.handle ?? null });
    found += 1;
  }
  return { found, searched: missing.length };
}

/** The banner's one click: re-grant every handle that needs it. */
export async function requestLocalAccess(scope = null) {
  const waiting = listLocalLibrary(scope).filter((entry) => localStatusOf(entry.id) === LOCAL_STATUS.PERMISSION);
  const granted = await requestEntryPermissions(waiting);
  await refreshPresence(waiting.map((entry) => entry.id));
  granted.forEach((id) => {
    const entry = entries.get(id);
    if (entry && !entry.peaks) queueAnalysis(id);
  });
  return granted.length;
}

// ── Collection membership ────────────────────────────────────────────────────────────────────────

/**
 * Add a collection to an entry's set. A no-op for a membership it already has, and for the `null`
 * scope, which is the absence of one rather than a collection to join.
 */
export async function addScopeToEntry(id, scope) {
  const entry = entries.get(id);
  if (!entry || !scope) return entry ?? null;
  // Read the stored row first, for the same reason removal does: two tabs each adding a different
  // collection from their own snapshot would have the second overwrite the first's membership.
  const stored = await readLocalEntry(id);
  if (stored) entries.set(id, stored);
  const current = scopesOf(stored ?? entry);
  if (current.includes(scope)) return stored ?? entry;
  const next = await applyEntryPatch(id, { scopes: [...current, scope] });
  emit();
  return next;
}

/**
 * Take an entry out of one collection.
 *
 * The bytes stay unless that was its last collection — a file two collections play is one file,
 * and leaving one of them must not delete what the other still uses. When it WAS the last one the
 * whole entry goes, which is what removing a file from the rail has always meant.
 *
 * @returns {Promise<{ removed: boolean, copyDeleted: boolean, remainingScopes: number }>}
 *   `removed` true means the entry and its bytes are gone; false with `remainingScopes` > 0 means
 *   it only left this collection.
 */
export async function removeLocalEntryFromScope(id, scope) {
  const entry = entries.get(id);
  if (!entry) return { removed: false, copyDeleted: false, remainingScopes: 0 };
  // Decided against the STORE, not against this tab's snapshot. Whether this was the last
  // collection is the difference between dropping a membership and deleting somebody's only copy
  // of a file — and another tab may have added a collection since this one last read. A snapshot
  // that has not seen that add would delete bytes the other tab is still playing.
  const stored = await readLocalEntry(id);
  const current = stored ? scopesOf(stored) : scopesOf(entry);
  if (stored) entries.set(id, stored);
  const remaining = current.filter((key) => key !== scope);
  if (remaining.length === 0) return { ...(await removeLocalEntry(id)), remainingScopes: 0 };
  await applyEntryPatch(id, { scopes: remaining });
  emit();
  return { removed: false, copyDeleted: false, remainingScopes: remaining.length };
}

/**
 * Take every file of one collection off this device. The collection's own membership is not
 * unpicked first: a file that also belongs elsewhere only leaves this collection, and one that
 * does not is removed with its bytes — the same rule as removing them one at a time.
 *
 * @returns {Promise<{ removed: number, left: number, copiesKept: number }>} `copiesKept` counts
 *   bytes the browser would not release, which whatever reports this must not describe as deleted.
 */
export async function removeLocalScope(scope) {
  const mine = listLocalLibrary(scope);
  let removed = 0;
  let left = 0;
  let copiesKept = 0;
  for (const entry of mine) {
    const result = await removeLocalEntryFromScope(entry.id, scope);
    if (result.removed) {
      removed += 1;
      if (!result.copyDeleted) copiesKept += 1;
    } else {
      left += 1;
    }
  }
  return { removed, left, copiesKept };
}

/**
 * Forget an entry and take its bytes with it.
 * @returns {Promise<{ removed: boolean, copyDeleted: boolean }>} `copyDeleted` false means a copy
 * is still on the disk, and whatever tells the person about this must not claim otherwise.
 */
export async function removeLocalEntry(id) {
  const entry = entries.get(id);
  if (!entry) return { removed: false, copyDeleted: false };
  cancelAnalysis(id);
  // Stop a copy that is queued or already running. Without this the job would finish after the
  // entry is gone, write the file back, and re-add its progress — the exact thing removal promised.
  copyAborts.get(id)?.abort();
  copyAborts.delete(id);
  copying.delete(id);
  copyFailures.delete(id);

  // The ENTRY goes first, then the bytes — not the other way round. If the store refuses the
  // delete, an entry that still says `copied` with its bytes already gone is unplayable and
  // unrecoverable; bytes left without an entry are only litter, and the boot sweep collects them.
  await deleteLocalEntry(id);
  entries.delete(id);
  // Whether the bytes really went. `deleteVaultCopy` counts "already absent" as gone, so a false
  // here means a copy is still on the disk — which the caller must not describe as deleted.
  //
  // Asked UNCONDITIONALLY, not only for an entry that says `copied`. A cover is kept whether or
  // not this browser took the audio — the desktop shell copies no audio at all — so gating this
  // on `copied` left the cover behind, and the header re-measures the vault after every removal
  // and would report bytes for a file the person had just deleted.
  const copyDeleted = await deleteVaultCopy(id);
  copiedBytes = await vaultUsageBytes();
  presence.delete(id);
  unregisterLocalByteSource(id);
  forgetSessionFile(id);
  const peaks = peaksUrls.get(id);
  if (peaks?.url) URL.revokeObjectURL(peaks.url);
  peaksUrls.delete(id);
  forgetCoverUrl(id);
  coverLoads.delete(id);
  emit();
  return { removed: true, copyDeleted };
}

// ── Duplicates left by the model that came before ────────────────────────────────────────────────

/**
 * The files this machine is holding more than once, from before an entry could belong to a set of
 * collections. Looking only — nothing here changes anything.
 */
export function findLocalDuplicates() {
  return groupLocalDuplicates(entries.values(), {
    isReachable: (entry) => presence.get(entry.id) !== 'missing',
  });
}

/**
 * Make each of these groups one file again.
 *
 * The survivor takes every collection the group belonged to, whatever any of them had been
 * analysed into, a way back to the file if it had none, and any orbiter or world nobody else had
 * chosen. Each collection's rail keeps its arrangement: the group's earliest place in that rail
 * becomes the survivor's. Then the other rows are removed the normal way, which is what takes
 * their copies with them.
 *
 * Three things make this safe enough to do to somebody's audio:
 *
 *   - it is asked for, group by group, by a person looking at the list — never a migration;
 *   - the whole pass holds the VAULT LOCK, so no other tab's copy or orphan sweep can interleave
 *     with a merge that is deleting files;
 *   - and inside that lock every row is re-read FROM THE STORE and re-checked against the identity
 *     the group was shown under. A group whose rows moved while the dialog was open is skipped
 *     rather than merged into something nobody agreed to.
 *
 * @param {Array<{ key: string, keep: object, duplicates: object[] }>} groups as `findLocalDuplicates`
 *   returned them
 * @returns {Promise<{ merged: number, removed: number, freedBytes: number, skipped: number }>}
 *   `freedBytes` counts only copies the browser really released — the header re-measures the disk
 *   either way, and a number that outran it would be the one thing this page cannot afford.
 */
export async function mergeLocalDuplicates(groups) {
  const result = { merged: 0, removed: 0, freedBytes: 0, skipped: 0 };
  const wanted = (Array.isArray(groups) ? groups : [groups]).filter((group) => group?.keep?.id);
  if (!wanted.length) return result;
  await withVaultLock(async () => {
    for (const group of wanted) {
      const ids = [group.keep.id, ...(group.duplicates ?? []).map((entry) => entry.id)];
      const stored = await Promise.all(ids.map((id) => readLocalEntry(id)));
      // A row that is gone, or that is no longer the file this group was shown under, means the
      // library moved under the dialog. Nothing partial: the whole group is left for next time.
      if (stored.some((row) => !row || localDuplicateKey(row) !== group.key)) {
        result.skipped += 1;
        continue;
      }
      stored.forEach((row) => entries.set(row.id, row));
      const [keep, ...duplicates] = stored;
      const scopes = [...new Set(stored.flatMap((row) => scopesOf(row)))];
      // The one thing a patch cannot carry: the cover is a file named after the entry that owns
      // it, so if the survivor has none and a duplicate does, the bytes move before that duplicate
      // is removed and takes its picture with it. Kilobytes, and inside the lock already held.
      let coverStored = keep.coverStored;
      if (!coverStored) {
        const source = duplicates.find((row) => row.coverStored);
        const bytes = source ? await readVaultCover(source.id) : null;
        if (bytes) coverStored = await writeVaultCover(keep.id, bytes);
      }
      const next = await applyEntryPatch(keep.id, {
        ...adoptionPatch(keep, duplicates),
        scopes,
        ...(coverStored ? { coverStored: true } : {}),
      });
      if (next) {
        forgetCoverUrl(keep.id);
        await primeEntry(next);
      }
      // Every rail any of these rows stood in, including the queue's own (`null`), which a file
      // that belongs to no collection can still be sitting in.
      const dropIds = duplicates.map((row) => row.id);
      for (const scope of [...scopes, null]) {
        const arrangement = await readLocalRailArrangement(scope);
        const ids = foldRailOrder(arrangement.ids, keep.id, dropIds);
        // A stage one of the merged rows was left on now belongs to the survivor — the same fold the
        // rail order gets, and the reason both live in one record: a stage still naming a row that
        // has just been deleted would come back as a placement pointing at nothing.
        const stages = {};
        let stagesFolded = false;
        for (const [index, id] of Object.entries(arrangement.stages)) {
          if (dropIds.includes(id)) stagesFolded = true;
          stages[index] = dropIds.includes(id) ? keep.id : id;
        }
        // The swaps ride along untouched: they are keyed by STAGE, and folding a duplicate into its
        // survivor changes which file a stage names, never what that stage plays. Dropping them
        // here would silently un-pair every stage the merge passed over.
        if (ids !== arrangement.ids || stagesFolded) {
          await writeLocalRailArrangement(scope, { ids, stages, swaps: arrangement.swaps });
        }
      }
      for (const row of duplicates) {
        const removal = await removeLocalEntry(row.id);
        if (!removal.removed) continue;
        result.removed += 1;
        if (row.copied && removal.copyDeleted) result.freedBytes += Number(row.size) || 0;
      }
      result.merged += 1;
    }
  });
  emit();
  return result;
}

// ── Rail arrangement ────────────────────────────────────────────────────────────────────────────

export function readLocalArrangement(scopeKey) {
  return readLocalRailArrangement(scopeKey);
}

/**
 * The local-files panel's own state, bounded the way the collection layout's sizes are: the panel is
 * a DESKTOP surface (a phone gets a full-width sheet), so a stored width never has to fit a screen
 * it was not set on. An absent value is "never said", which reads as the panel's own default.
 */
const MIN_LOCAL_PANEL_WIDTH = 320;
const MAX_LOCAL_PANEL_WIDTH = 720;

function sanitizePanel(panel) {
  const out = {};
  if (typeof panel?.open === 'boolean') out.open = panel.open;
  if (Number.isFinite(panel?.width)) {
    out.width = Math.min(MAX_LOCAL_PANEL_WIDTH, Math.max(MIN_LOCAL_PANEL_WIDTH, Math.round(panel.width)));
  }
  return out;
}

/** The local-files panel's own state, written on its own — see `writeLocalRailPanel`. */
export function writeLocalPanel(scopeKey, panel) {
  return writeLocalRailPanel(scopeKey, sanitizePanel(panel));
}

export function writeLocalArrangement(scopeKey, { ids, stages, swaps } = {}) {
  // Only a local id may name a stage here. The stages hosted tracks hold are the collection's
  // layout's to describe, and this record must never claim one of them.
  const stagesOut = Object.fromEntries(
    Object.entries(stages ?? {}).filter(([, id]) => isLocalEntryId(id)),
  );
  return writeLocalRailArrangement(scopeKey, {
    ids: (Array.isArray(ids) ? ids : []).filter(isLocalEntryId),
    stages: stagesOut,
    // A swap describes a stage, so it is only ever kept for a stage this record actually names —
    // otherwise a placement removed here would leave a pairing behind pointing at nothing. The ids
    // inside are ORBITER and WORLD ids, which are ordinary platform ids and not local ones.
    swaps: Object.fromEntries(
      Object.entries(swaps ?? {})
        .filter(([index, swap]) => stagesOut[index] && swap && typeof swap === 'object')
        .map(([index, swap]) => {
          const shaped = {};
          if (typeof swap.orbiterId === 'string' && swap.orbiterId) shaped.orbiterId = swap.orbiterId;
          if (typeof swap.entangledWorldId === 'string' && swap.entangledWorldId) {
            shaped.entangledWorldId = swap.entangledWorldId;
          }
          return [index, shaped];
        })
        .filter(([, shaped]) => shaped.orbiterId || shaped.entangledWorldId),
    ),
  });
}

// ── Tests ────────────────────────────────────────────────────────────────────────────────────────

export function resetLocalLibraryForTests() {
  entries.clear();
  scopeNames.clear();
  scopeNameFailures.clear();
  presence.clear();
  peaksUrls.clear();
  coverUrls.clear();
  coverLoads.clear();
  copying.clear();
  copyFailures.clear();
  copyChain = Promise.resolve();
  copiedBytes = 0;
  listeners.clear();
  booted = null;
  resetDefaultPair();
  backfilledTags = false;
}
